import api from '../../utils/api'
import { useState, useEffect, useMemo } from "react"
import { FaFilter } from 'react-icons/fa'
import { FiInbox } from 'react-icons/fi'
import { VscClearAll } from 'react-icons/vsc'

/* CSS */

import '../../shared/components/form/inputDefault.css'

/* form */
import Input from "../../shared/components/form/input"

/* Hooks */
import useMyPermission from '../../hooks/useMyPermission'

import ListClientRow from './ListRow/ListClientRow'
import Modal from '../../shared/modal/Modal'
import NewClient from './NewClient'
import Button from '../../shared/components/Button/Button'
import ClientFilterModal from './ClientFilterModal'

const EMPTY_FILTERS = { personType: 'all', status: 'all', seller: '' }

function Client() {

    const [token] = useState(localStorage.getItem('token') || '')
    const [clients, setClients] = useState([])
    const [modalNew, setModalNew] = useState(false)
    const [modalFilter, setModalFilter] = useState(false)
    const [filters, setFilters] = useState(EMPTY_FILTERS)

    const [search, setSearch] = useState("")
    const [load, setLoad] = useState(false)

    /* ---------------------- Permission ------------------------- */
    const [myPermission, setMyPermission] = useState({ group: "", })
    const { checkPermission } = useMyPermission()
    const myPermissionNew = checkPermission(myPermission, "client_create")
    const myPermissionView = checkPermission(myPermission, "client_view")
    const myPermissionEdit = checkPermission(myPermission, "client_edit")
    const myPermissionDelete = checkPermission(myPermission, "client_delete")

    /* get all function */
    const getAll = () => {
        api.get(`/client/getall`, {
            headers: {
                Authorization: `Bearer ${JSON.parse(token)}`,
            },
        }).then((response) => {
            if (response) {
                setClients(response.data.clients)
                setMyPermission(response.data.permission)
                setLoad(true)
            }
        })
    }

    /* use effect */
    useEffect(() => {
        getAll()
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token])

    /* ----------------------- Search / filter function ---------------------------------- */
    const filter = useMemo(() => {
        const searchLowerCase = search.toLowerCase()
        const searchDigits = search.replace(/\D/g, '')
        return clients.filter((item) => {
            const matchSearch = item.name.toLowerCase().includes(searchLowerCase) ||
                (searchDigits && (
                    (item.CPF && item.CPF.replace(/\D/g, '').includes(searchDigits)) ||
                    (item.CNPJ && item.CNPJ.replace(/\D/g, '').includes(searchDigits))
                ))
            const matchPersonType = filters.personType === 'all' || item.personType === filters.personType
            const matchStatus =
                filters.status === 'all' ? true :
                filters.status === 'active' ? !item.disabled :
                    !!item.disabled
            const matchSeller = !filters.seller || (item.associatedUsers || []).some((u) => String(u._id) === String(filters.seller))
            return matchSearch && matchPersonType && matchStatus && matchSeller
        })
    }, [clients, search, filters])

    function handleSearch(e) {
        setSearch(e.target.value)
    }

    const sellerOptions = useMemo(() => {
        const map = new Map()
        clients.forEach((c) => (c.associatedUsers || []).forEach((u) => { if (u && u._id) map.set(String(u._id), u.name) }))
        return Array.from(map, ([value, label]) => ({ value, label })).sort((a, b) => a.label.localeCompare(b.label))
    }, [clients])

    const hasActiveFilter = !!search || filters.personType !== 'all' || filters.status !== 'all' || !!filters.seller

    const handleFilterChange = (key, value) => setFilters(f => ({ ...f, [key]: value }))
    const clearFilters = () => { setFilters(EMPTY_FILTERS); setSearch('') }

    if (!load) {
        return <div className="pg-loading">Carregando clientes...</div>
    }

    /* PAGE */
    return myPermissionView ? (
        <section className="pg-page">

            <div className="pg-sticky-top">
                <div className="pg-header">
                    <div className="pg-header-title">
                        <p className="pg-title">Clientes</p>
                        <span className="pg-title-accent" />
                    </div>
                    <div className="pg-header-row">
                        <div className="pg-header-actions">
                            {myPermissionNew && (
                                <Button variant="new" onClick={() => setModalNew(true)}>Cadastrar</Button>
                            )}
                        </div>
                        <div className="pg-header-right">
                            <button
                                className={`pg-filter-btn ${hasActiveFilter ? 'pg-filter-btn-active' : ''}`}
                                onClick={() => setModalFilter(true)}
                            >
                                <FaFilter size={13} />
                                <span>Filtrar</span>
                                {hasActiveFilter && <span className="pg-filter-dot" />}
                            </button>
                            <div className="pg-search">
                                <Input
                                    type="text"
                                    name="search"
                                    placeholder="Buscar por nome, CPF ou CNPJ..."
                                    value={search}
                                    handleOnChange={handleSearch}
                                />
                            </div>
                            <span className="pg-count">
                                {filter.length} {filter.length === 1 ? 'cliente' : 'clientes'}
                            </span>
                        </div>
                    </div>
                </div>

                {hasActiveFilter && (
                    <div className="pg-filter-active-bar">
                        <span className="pg-filter-active-dot" />
                        <span className="pg-filter-active-label">Filtro ativo</span>
                        <span className="pg-filter-active-count">
                            {filter.length} {filter.length === 1 ? 'cliente encontrado' : 'clientes encontrados'}
                        </span>
                        <button className="pg-filter-active-clear" onClick={clearFilters}>
                            <VscClearAll size={14} />
                            Limpar
                        </button>
                    </div>
                )}
            </div>

            <div className="pg-list">
                {filter.length > 0 ? (
                    <ListClientRow
                        sellerEnabled={true}
                        dataArray={filter}
                        handleOnSuccess={getAll}
                        myPermissionEdit={myPermissionEdit}
                        myPermissionDelete={myPermissionDelete}
                        myPermissionView={myPermissionView}
                    />
                ) : (
                    <div className="pg-empty">
                        <FiInbox size={32} className="pg-empty-icon" />
                        <p className="pg-empty-title">Nenhum cliente encontrado</p>
                        <p className="pg-empty-subtitle">
                            {clients.length === 0
                                ? 'Cadastre o primeiro cliente para começar.'
                                : 'Tente ajustar a busca ou os filtros.'}
                        </p>
                    </div>
                )}
            </div>

            <Modal
                modalStatus={modalNew}
                changeStatus={setModalNew}
                t={3}
                w={88}
                h={90}
                content={
                    <NewClient
                        btCancelAction={() => setModalNew(false)}
                        handleOnSuccess={() => { setModalNew(false); getAll() }}
                    />
                }
            />

            {modalFilter && (
                <ClientFilterModal
                    filters={filters}
                    onChange={handleFilterChange}
                    onClear={clearFilters}
                    onClose={() => setModalFilter(false)}
                    sellerOptions={sellerOptions}
                    showSellerFilter={true}
                />
            )}

        </section>
    ) : (
        <div className="pg-empty">
            <p className="pg-empty-title">Sem permissão para visualizar clientes</p>
        </div>
    )
}

export default Client
